import React, { useState, useEffect, useRef } from 'react';
import { X, Eye, EyeOff, Mail, Lock, User, Phone, Car, ArrowRight, Check, AlertCircle, Loader } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import './AuthModal.css';

export default function AuthModal() {
  const { authModal, authRedirect, login, signup, closeAuth, user } = useAuth();
  const navigate = useNavigate();

  const [tab,      setTab]      = useState('login');
  const [showPass, setShowPass] = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState(false);

  const [loginForm,  setLoginForm]  = useState({ email: '', password: '' });
  const [signupForm, setSignupForm] = useState({ name: '', email: '', phone: '', password: '', vehicle: '' });

  // Track when modal opens so we reset form only on open, not on re-renders
  const prevModal = useRef(null);
  useEffect(() => {
    if (authModal && prevModal.current !== authModal) {
      setTab(authModal === 'signup' ? 'signup' : 'login');
      setError('');
      setSuccess(false);
      setShowPass(false);
      setLoading(false);
      setLoginForm({ email: '', password: '' });
      setSignupForm({ name: '', email: '', phone: '', password: '', vehicle: '' });
      document.body.style.overflow = 'hidden';
    }
    if (!authModal) {
      document.body.style.overflow = '';
    }
    prevModal.current = authModal;
    return () => { document.body.style.overflow = ''; };
  }, [authModal]);

  // Auto-close when user becomes logged in (reactive to auth state)
  useEffect(() => {
    if (user && authModal) {
      closeAuth();
      if (authRedirect) navigate(authRedirect);
    }
  }, [user, authModal, authRedirect, closeAuth, navigate]);

  if (!authModal) return null;

  const switchTab = newTab => {
    setTab(newTab);
    setError('');
    setSuccess(false);
    setShowPass(false);
  };

  /* ── Login ── */
  const handleLogin = async e => {
    e.preventDefault();
    if (!loginForm.email.trim() || !loginForm.password) {
      setError('Please enter your email and password.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const result = await login(loginForm.email.trim(), loginForm.password);
      if (!result.success) {
        setError(result.error || 'Invalid email or password. Please try again.');
        setLoading(false);
        return;
      }
      setSuccess(true);
      // closeAuth handled by the useEffect above when user state updates
    } catch (err) {
      setError('Login failed. Please check your connection and try again.');
      setLoading(false);
    }
  };

  /* ── Signup ── */
  const handleSignup = async e => {
    e.preventDefault();
    // Client-side validation
    if (!signupForm.name.trim()) { setError('Full name is required.'); return; }
    if (!signupForm.email.trim()) { setError('Email address is required.'); return; }
    if (!signupForm.phone.trim()) { setError('Phone number is required.'); return; }
    if (!signupForm.password)    { setError('Password is required.'); return; }
    if (signupForm.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(signupForm.email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const result = await signup({
        name:     signupForm.name.trim(),
        email:    signupForm.email.trim().toLowerCase(),
        phone:    signupForm.phone.trim(),
        password: signupForm.password,
        vehicle:  signupForm.vehicle.trim(),
      });
      if (!result.success) {
        // Show specific backend errors (email already exists, phone taken, etc.)
        setError(result.error || 'Registration failed. Please try again.');
        setLoading(false);
        return;
      }
      setSuccess(true);
      // closeAuth handled by useEffect when user state updates
    } catch (err) {
      setError('Registration failed. Please check your connection and try again.');
      setLoading(false);
    }
  };

  return (
    <div className="auth-overlay" onClick={() => { if (!loading) closeAuth(); }}>
      <div className="auth-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="auth-modal__header">
          <div className="auth-modal__logo">
            <div className="auth-modal__logo-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 7l10 5 10-5-10-5z" fill="currentColor"/>
                <path d="M2 17l10 5 10-5" stroke="currentColor" strokeWidth="2" fill="none"/>
                <path d="M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" fill="none"/>
              </svg>
            </div>
            <span className="auth-modal__logo-name">AutoElite</span>
          </div>
          <button className="auth-modal__close" onClick={closeAuth} disabled={loading} aria-label="Close">
            <X size={18}/>
          </button>
        </div>

        {/* Tab bar */}
        <div className="auth-tabs">
          <button className={`auth-tab ${tab === 'login'  ? 'active' : ''}`} onClick={() => switchTab('login')}  disabled={loading || success}>
            Sign In
          </button>
          <button className={`auth-tab ${tab === 'signup' ? 'active' : ''}`} onClick={() => switchTab('signup')} disabled={loading || success}>
            Create Account
          </button>
        </div>

        {/* Success screen */}
        {success && (
          <div className="auth-success">
            <div className="auth-success__icon"><Check size={32}/></div>
            <h3>{tab === 'login' ? 'Welcome back!' : 'Account created!'}</h3>
            <p>Signing you in…</p>
            <div className="auth-success__bar"/>
          </div>
        )}

        {/* ── LOGIN FORM ── */}
        {!success && tab === 'login' && (
          <form className="auth-form" onSubmit={handleLogin} noValidate>
            <div className="auth-form__title">
              <h2>Welcome back</h2>
              <p>Sign in to book services and track your vehicle.</p>
            </div>

            {/* Error banner */}
            {error && (
              <div className="auth-error">
                <AlertCircle size={15}/>
                <span>{error}</span>
              </div>
            )}

            <div className="auth-field">
              <label className="label">Email Address</label>
              <div className="auth-input-wrap">
                <Mail size={15} className="auth-input-icon"/>
                <input
                  className={`input auth-input ${error && !loginForm.email ? 'input--error' : ''}`}
                  type="email"
                  placeholder="you@email.com"
                  value={loginForm.email}
                  onChange={e => { setLoginForm(f => ({ ...f, email: e.target.value })); setError(''); }}
                  required
                  autoFocus
                  disabled={loading}
                />
              </div>
            </div>

            <div className="auth-field">
              <label className="label">Password</label>
              <div className="auth-input-wrap">
                <Lock size={15} className="auth-input-icon"/>
                <input
                  className={`input auth-input ${error && !loginForm.password ? 'input--error' : ''}`}
                  type={showPass ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={loginForm.password}
                  onChange={e => { setLoginForm(f => ({ ...f, password: e.target.value })); setError(''); }}
                  required
                  disabled={loading}
                />
                <button type="button" className="auth-pass-toggle" onClick={() => setShowPass(v => !v)} tabIndex={-1}>
                  {showPass ? <EyeOff size={15}/> : <Eye size={15}/>}
                </button>
              </div>
            </div>

            <button className="btn btn-primary auth-submit" type="submit" disabled={loading}>
              {loading ? <><Loader size={15} className="auth-spin"/> Signing in…</> : <><ArrowRight size={15}/> Sign In</>}
            </button>

            <p className="auth-switch">
              Don't have an account?{' '}
              <button type="button" onClick={() => switchTab('signup')} disabled={loading}>Create one free</button>
            </p>
          </form>
        )}

        {/* ── SIGNUP FORM ── */}
        {!success && tab === 'signup' && (
          <form className="auth-form" onSubmit={handleSignup} noValidate>
            <div className="auth-form__title">
              <h2>Join AutoElite</h2>
              <p>Create a free account to book and track your vehicle services.</p>
            </div>

            {error && (
              <div className="auth-error">
                <AlertCircle size={15}/>
                <span>{error}</span>
              </div>
            )}

            <div className="auth-grid">
              <div className="auth-field">
                <label className="label">Full Name *</label>
                <div className="auth-input-wrap">
                  <User size={15} className="auth-input-icon"/>
                  <input
                    className="input auth-input"
                    placeholder="Harpreet Singh"
                    value={signupForm.name}
                    onChange={e => { setSignupForm(f => ({ ...f, name: e.target.value })); setError(''); }}
                    required autoFocus disabled={loading}
                  />
                </div>
              </div>
              <div className="auth-field">
                <label className="label">Phone Number *</label>
                <div className="auth-input-wrap">
                  <Phone size={15} className="auth-input-icon"/>
                  <input
                    className="input auth-input"
                    placeholder="+91 98765 43210"
                    value={signupForm.phone}
                    onChange={e => { setSignupForm(f => ({ ...f, phone: e.target.value })); setError(''); }}
                    required disabled={loading}
                  />
                </div>
              </div>
            </div>

            <div className="auth-field">
              <label className="label">Email Address *</label>
              <div className="auth-input-wrap">
                <Mail size={15} className="auth-input-icon"/>
                <input
                  className="input auth-input"
                  type="email"
                  placeholder="you@email.com"
                  value={signupForm.email}
                  onChange={e => { setSignupForm(f => ({ ...f, email: e.target.value })); setError(''); }}
                  required disabled={loading}
                />
              </div>
            </div>

            <div className="auth-field">
              <label className="label">Password * <span style={{ fontWeight:400, textTransform:'none', letterSpacing:0, fontSize:10, color:'var(--text-muted)' }}>min 8 characters</span></label>
              <div className="auth-input-wrap">
                <Lock size={15} className="auth-input-icon"/>
                <input
                  className="input auth-input"
                  type={showPass ? 'text' : 'password'}
                  placeholder="Create a strong password"
                  value={signupForm.password}
                  onChange={e => { setSignupForm(f => ({ ...f, password: e.target.value })); setError(''); }}
                  required disabled={loading}
                />
                <button type="button" className="auth-pass-toggle" onClick={() => setShowPass(v => !v)} tabIndex={-1}>
                  {showPass ? <EyeOff size={15}/> : <Eye size={15}/>}
                </button>
              </div>
              {/* Password strength indicator */}
              {signupForm.password.length > 0 && (
                <div className="auth-strength">
                  {['', '', '', ''].map((_, i) => (
                    <div key={i} className={`auth-strength__bar ${
                      signupForm.password.length >= 8 + i * 2 ? 'auth-strength__bar--fill' : ''
                    }`}
                    style={{ background: signupForm.password.length >= 12 ? 'var(--success)' :
                             signupForm.password.length >= 8 ? '#F59E0B' : 'var(--danger)' }}/>
                  ))}
                  <span className="auth-strength__label">
                    {signupForm.password.length < 8  ? 'Weak' :
                     signupForm.password.length < 12 ? 'Good' : 'Strong'}
                  </span>
                </div>
              )}
            </div>

            <div className="auth-field">
              <label className="label">
                Vehicle{' '}
                <span style={{ fontWeight:400, textTransform:'none', letterSpacing:0, fontSize:10, color:'var(--text-muted)' }}>
                  (optional)
                </span>
              </label>
              <div className="auth-input-wrap">
                <Car size={15} className="auth-input-icon"/>
                <input
                  className="input auth-input"
                  placeholder="e.g. BMW 3 Series · PB10 XX 0000"
                  value={signupForm.vehicle}
                  onChange={e => setSignupForm(f => ({ ...f, vehicle: e.target.value }))}
                  disabled={loading}
                />
              </div>
            </div>

            <button className="btn btn-primary auth-submit" type="submit" disabled={loading}>
              {loading ? <><Loader size={15} className="auth-spin"/> Creating account…</> : <><Check size={15}/> Create Account</>}
            </button>

            <p className="auth-switch">
              Already have an account?{' '}
              <button type="button" onClick={() => switchTab('login')} disabled={loading}>Sign in</button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}